/**
 * @author        julian@zumper.com (Julian Napolitan)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   Contact Component for New Listables Experiment
 */
/** @jsx jsx */

import { Button } from '@chakra-ui/react'
import { jsx } from '@emotion/react'
import { Z_INDEX_GROUP_1 } from '@zcode-ui/themes'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import type { Listable, Listing } from '@zcode/state-type'
import React, { type HTMLAttributes } from 'react'
import { TourButtonContainer } from '../../../../EqualCTAs/TourButton/TourButtonContainer'
import { MessageButtonContainer } from '../../../../MessageButton/MessageButtonContainer'
import { getShouldLimitLeads } from '../../../../message/MessageForm/helpers/getShouldLimitLeads'
import { infoOverlay } from '../ListItem/DesktopView/style'
import * as styles from './style'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  analyticChannel:
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
  entity: Listable | Listing
  isInfoOverlay?: boolean
  longTermLeadCount: number
  onPaidPhoneClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  origin: Origin
  shortTermLeadCount: number
}

const Contact = ({
  analyticChannel,
  entity,
  isInfoOverlay,
  longTermLeadCount,
  onPaidPhoneClick,
  origin,
  shortTermLeadCount,
  ...rest
}: Props) => {
  // TODO: I 'fixed' this, which created unexpected behavior. Figure out whether we ever want to show the
  // call now button.
  const isMobile = undefined
  const { paidPhone, paidPhoneHref, isExternal, isMessageable, isShortTermAvailability } = entity
  const showPhone = paidPhone && paidPhoneHref && !isExternal && isMobile

  const showMessageButton = isMessageable || isExternal

  const shouldLimitLeads = getShouldLimitLeads({ longTermLeadCount, shortTermLeadCount })

  const messageText = 'Message'

  return (
    <div css={styles.contact} {...rest}>
      <div css={styles.buttonContainer}>
        {showMessageButton && !isShortTermAvailability && !shouldLimitLeads ? (
          <div css={styles.ctaContainer}>
            <TourButtonContainer
              analyticChannel={analyticChannel}
              btnCss={[
                styles.messageBtn,
                styles.msgBtn,
                showPhone && styles.hasPhone,
                isInfoOverlay && styles.isInfoOverlay,
              ]}
              entity={entity}
              origin={origin}
            />
            {showPhone ? (
              <a
                css={[styles.phoneBtn, isInfoOverlay && styles.isInfoOverlay]}
                href={paidPhoneHref}
                onClick={onPaidPhoneClick}
              >
                Call {isInfoOverlay ? paidPhone : 'now'}
              </a>
            ) : (
              <MessageButtonContainer
                analyticChannel={analyticChannel}
                css={[
                  styles.msgBtn,
                  showPhone && styles.hasPhone,
                  isInfoOverlay && styles.isInfoOverlay,
                ]}
                entity={entity}
                isMessagedText={isMobile ? 'Messaged' : 'Message Sent'}
                origin={origin}
                showAgentInfo
                text={messageText}
                usePrimaryButton
              />
            )}
          </div>
        ) : (
          <Button<'button'>
            alignItems="center"
            display={isInfoOverlay ? 'unset' : 'flex'}
            flex="2 2"
            fontWeight="bold"
            justifyContent="center"
            size="md"
            variant="secondary"
            width={infoOverlay ? '315px' : '100%'}
            zIndex={Z_INDEX_GROUP_1}
          >
            See details
          </Button>
        )}
      </div>
    </div>
  )
}

export { Contact }
